<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import {
  CONTACT_TABLE_COLUMNS,
  DEFAULT_COLUMN_KEYS,
  buildCustomAttributeColumns,
} from 'dashboard/routes/dashboard/contacts/helper/contactTableColumns';

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const isMenuOpen = ref(false);

const contactAttributes = useMapGetter('attributes/getContactAttributes');

const customColumns = computed(() =>
  buildCustomAttributeColumns(contactAttributes.value || [])
);

// Built-in columns the user can toggle (everything except always-visible ones).
const builtinColumns = computed(() =>
  CONTACT_TABLE_COLUMNS.map(column => ({
    ...column,
    label: t(column.labelKey),
  }))
);

const selectedKeys = computed(() => {
  const saved = uiSettings.value?.contacts_table_columns;
  return Array.isArray(saved) && saved.length ? saved : DEFAULT_COLUMN_KEYS;
});

const isSelected = key => selectedKeys.value.includes(key);

const toggleColumn = (column, checked) => {
  if (column.alwaysVisible) return;
  const withoutKey = selectedKeys.value.filter(key => key !== column.key);
  const next = checked ? [...withoutKey, column.key] : withoutKey;
  updateUISettings({ contacts_table_columns: next });
};
</script>

<template>
  <div v-on-clickaway="() => (isMenuOpen = false)" class="relative">
    <Button
      icon="i-lucide-columns-3"
      color="slate"
      size="sm"
      variant="ghost"
      :title="t('CONTACTS_LAYOUT.TABLE.COLUMNS_MENU.LABEL')"
      :aria-label="t('CONTACTS_LAYOUT.TABLE.COLUMNS_MENU.LABEL')"
      :class="isMenuOpen ? 'bg-n-alpha-2' : ''"
      @click="isMenuOpen = !isMenuOpen"
    />
    <div
      v-if="isMenuOpen"
      class="absolute top-full mt-1 ltr:right-0 rtl:left-0 z-50 flex flex-col gap-3 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-64 rounded-xl p-3 max-h-96 overflow-y-auto"
    >
      <div class="flex flex-col gap-1">
        <span
          class="px-1 text-xs font-medium uppercase text-n-slate-10 tracking-wide"
        >
          {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS_MENU.FIELDS') }}
        </span>
        <label
          v-for="column in builtinColumns"
          :key="column.key"
          class="flex items-center gap-2 px-1 py-1.5 rounded-md cursor-pointer hover:bg-n-alpha-1"
          :class="{ 'opacity-60 cursor-not-allowed': column.alwaysVisible }"
        >
          <Checkbox
            :model-value="isSelected(column.key)"
            :disabled="column.alwaysVisible"
            @change="event => toggleColumn(column, event.target.checked)"
          />
          <span class="text-sm text-n-slate-12 truncate">
            {{ column.label }}
          </span>
        </label>
      </div>

      <div v-if="customColumns.length" class="flex flex-col gap-1">
        <span
          class="px-1 text-xs font-medium uppercase text-n-slate-10 tracking-wide"
        >
          {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS_MENU.CUSTOM_ATTRIBUTES') }}
        </span>
        <label
          v-for="column in customColumns"
          :key="column.key"
          class="flex items-center gap-2 px-1 py-1.5 rounded-md cursor-pointer hover:bg-n-alpha-1"
        >
          <Checkbox
            :model-value="isSelected(column.key)"
            @change="event => toggleColumn(column, event.target.checked)"
          />
          <span class="text-sm text-n-slate-12 truncate">
            {{ column.label }}
          </span>
        </label>
      </div>
    </div>
  </div>
</template>
